import React, { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FaPlus, FaUpload, FaTimes, FaImage } from 'react-icons/fa';

interface UploadButtonProps {
  onUploadComplete?: () => void;
}

const UploadButton: React.FC<UploadButtonProps> = ({ onUploadComplete }) => {
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [language, setLanguage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const user = getCurrentUser();
  const navigate = useNavigate();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      
      // Clean up previous preview URL if it exists
      return () => {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
      };
    }
  }, [previewUrl]);

  const onThumbnailDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedThumbnail(file);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setThumbnailPreviewUrl(url);
      
      // Clean up previous preview URL if it exists
      return () => {
        if (thumbnailPreviewUrl) {
          URL.revokeObjectURL(thumbnailPreviewUrl);
        }
      };
    }
  }, [thumbnailPreviewUrl]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.mkv']
    },
    maxSize: 350 * 1024 * 1024, // 350MB max
    multiple: false
  });

  const { getRootProps: getThumbnailRootProps, getInputProps: getThumbnailInputProps } = useDropzone({
    onDrop: onThumbnailDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png']
    },
    maxSize: 5 * 1024 * 1024, // 5MB max
    multiple: false
  });

  const handleUpload = async () => {
    if (!user || !selectedFile) return;

    try {
      setUploading(true);

      // Check user's balance
      const { data: userData, error: userError } = await supabase
        .from('ponousers')
        .select('wallet_balance')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;

      if (userData.wallet_balance < 1) {
        toast.error('Insufficient balance. You need $1 to upload a video.');
        navigate('/wallet');
        return;
      }

      // Upload video file
      const videoFileExt = selectedFile.name.split('.').pop();
      const videoFileName = `${user.id}/${Date.now()}.${videoFileExt}`;
      const { error: videoUploadError } = await supabase.storage
        .from('videospono')
        .upload(videoFileName, selectedFile);

      if (videoUploadError) throw videoUploadError;

      // Get video URL
      const { data: { publicUrl: videoUrl } } = supabase.storage
        .from('videospono')
        .getPublicUrl(videoFileName);

      // Upload thumbnail if provided
      let thumbnailUrl = null;
      if (selectedThumbnail) {
        const thumbnailFileExt = selectedThumbnail.name.split('.').pop();
        const thumbnailFileName = `${user.id}/${Date.now()}_thumb.${thumbnailFileExt}`;
        const { error: thumbnailUploadError } = await supabase.storage
          .from('videospono')
          .upload(thumbnailFileName, selectedThumbnail);

        if (thumbnailUploadError) throw thumbnailUploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('videospono')
          .getPublicUrl(thumbnailFileName);
        
        thumbnailUrl = publicUrl;
      }

      // Deduct $1 from user's wallet
      const { error: walletError } = await supabase
        .from('ponousers')
        .update({ wallet_balance: userData.wallet_balance - 1 })
        .eq('id', user.id);

      if (walletError) throw walletError;

      // Create transaction record
      const { error: transactionError } = await supabase
        .from('ponotransactions')
        .insert({
          user_id: user.id,
          amount: 1,
          type: 'withdraw',
          status: 'completed',
          description: 'Video upload fee'
        });

      if (transactionError) throw transactionError;

      // Create video record
      const { error: videoError } = await supabase
        .from('ponavideos')
        .insert({
          user_id: user.id,
          video_url: videoUrl,
          thumbnail_url: thumbnailUrl,
          title,
          description,
          category,
          language,
          is_visible: true
        });

      if (videoError) throw videoError;

      // Update local user data
      const updatedUser = {
        ...user,
        wallet_balance: userData.wallet_balance - 1
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      toast.success('Video uploaded successfully!');
      setShowModal(false);
      setSelectedFile(null);
      setSelectedThumbnail(null);
      setPreviewUrl(null);
      setThumbnailPreviewUrl(null);
      setTitle('');
      setDescription('');
      setCategory('');
      setLanguage('');
      if (onUploadComplete) onUploadComplete();
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    if (thumbnailPreviewUrl) {
      URL.revokeObjectURL(thumbnailPreviewUrl);
    }
    setShowModal(false);
    setSelectedFile(null);
    setSelectedThumbnail(null);
    setPreviewUrl(null);
    setThumbnailPreviewUrl(null);
    setTitle('');
    setDescription('');
    setCategory('');
    setLanguage('');
  };

  const categories = [
    'Big Ass',
    'Ebony',
    'Black Beauty',
    'Sexy Girl',
    'Lesbian',
    'Porno star',
    'young girl',
    'Africans',
    'Americans',
    'Asian'
  ];

  const languages = [
    'english',
    'spanish',
    'french'
  ];

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-black p-4 rounded-full shadow-lg hover:bg-yellow-500 transition-colors z-50"
      >
        <FaPlus className="w-6 h-6" />
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-gray-900 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 p-4 border-b border-gray-800 flex justify-between items-center z-10">
              <h3 className="text-xl font-semibold text-white">Upload Video ($1 fee)</h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-white p-2"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Video File
                </label>
                <div
                  {...getRootProps()}
                  className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-yellow-400 transition-colors"
                >
                  <input {...getInputProps()} />
                  {selectedFile ? (
                    <div className="text-gray-300">
                      {previewUrl && (
                        <video
                          ref={videoRef}
                          src={previewUrl}
                          className="max-h-40 mx-auto mb-2"
                          controls
                        />
                      )}
                      <p className="text-sm">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Click or drag to replace
                      </p>
                    </div>
                  ) : (
                    <div className="text-gray-400 py-8">
                      <FaUpload className="w-8 h-8 mx-auto mb-2" />
                      <p>Click or drag video to upload</p>
                      <p className="text-sm mt-1">Maximum size: 350MB</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Thumbnail Image
                </label>
                <div
                  {...getThumbnailRootProps()}
                  className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-yellow-400 transition-colors"
                >
                  <input {...getThumbnailInputProps()} />
                  {selectedThumbnail ? (
                    <div className="text-gray-300">
                      <img
                        src={thumbnailPreviewUrl || ''}
                        alt="Thumbnail preview"
                        className="max-h-40 mx-auto mb-2 object-contain"
                      />
                      <p className="text-sm">{selectedThumbnail.name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Click or drag to replace
                      </p>
                    </div>
                  ) : (
                    <div className="text-gray-400 py-8">
                      <FaImage className="w-8 h-8 mx-auto mb-2" />
                      <p>Click or drag image to upload thumbnail</p>
                      <p className="text-sm mt-1">Maximum size: 5MB</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="Enter video title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="Enter video description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Language
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  required
                >
                  <option value="">Select a language</option>
                  {languages.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang.charAt(0).toUpperCase() + lang.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-900 p-4 border-t border-gray-800 flex flex-col sm:flex-row gap-2 sm:gap-4 justify-end">
              <button
                onClick={handleClose}
                className="w-full sm:w-auto px-4 py-2 text-gray-400 hover:text-white text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || !title || !category || !language || uploading}
                className="w-full sm:w-auto px-6 py-2 bg-yellow-400 text-black rounded-lg text-sm font-medium hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Upload Video ($1)'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadButton;